import React, { memo, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { KpiCard, KpiGrid } from 'molecules';
import { buildStudentMoney, ICurrencyTotals } from 'core/utils/studentMoney';

import { formatMoneyCurrency } from './parts/format';
import { StudentColors } from './parts/studentColors';
import { IStudent } from './parts';

interface IKpiStripProps {
  student: IStudent;
}

/**
 * Per-currency KPI strip above the tab bar (Area 5, Req 14–15).
 *
 * Totals are computed per currency by `buildStudentMoney` (terminated agreements
 * excluded, string decimals coerced). With a single currency the classic 4-KPI
 * layout is used; with multiple currencies each currency's Budget/Paid/Debt is a
 * separate readable entry — never a `·`-joined string (Req 14.1–14.2). Paid
 * carries the success accent and Debt>0 the warn accent in both layouts
 * (Req 14.3–14.5); accents come from `StudentColors` tokens (Req 14.7).
 */
export const KpiStrip = memo(({ student }: IKpiStripProps) => {
  const { t } = useTranslation();

  const rows: ICurrencyTotals[] = useMemo(
    () => buildStudentMoney(student.agreements || []),
    [student.agreements],
  );

  const paymentsCount = useMemo(
    () =>
      (student.agreements || []).reduce(
        (acc, a) => acc + (a?.payments?.length || 0),
        0,
      ),
    [student.agreements],
  );

  const agreementsActiveTotal = rows.reduce(
    (acc, r) => acc + r.agreementsActive,
    0,
  );

  const isMulti = rows.length > 1;
  const primary = rows[0];

  if (isMulti) {
    // Multi-currency: one readable row per currency inside the Budget/Paid/Debt
    // cards, each formatted with its own currency and keeping its accent.
    return (
      <Wrap>
        <KpiGrid columns={4}>
          <KpiCard
            label={t('pages.studentDetail.kpi.budget')}
            value={
              <PerCurrencyList>
                {rows.map((r) => (
                  <PerCurrencyItem key={r.currency}>
                    {formatMoneyCurrency(r.budget, r.currency)}
                  </PerCurrencyItem>
                ))}
              </PerCurrencyList>
            }
            subLabel={t('pages.studentDetail.kpi.agreementsActive', {
              count: agreementsActiveTotal,
            })}
          />
          <KpiCard
            label={t('pages.studentDetail.kpi.paid')}
            value={
              <PerCurrencyList>
                {rows.map((r) => (
                  <PerCurrencyItem
                    key={r.currency}
                    tone={StudentColors.success}
                  >
                    {formatMoneyCurrency(r.paid, r.currency)}
                  </PerCurrencyItem>
                ))}
              </PerCurrencyList>
            }
          />
          <KpiCard
            label={t('pages.studentDetail.kpi.debt')}
            value={
              <PerCurrencyList>
                {rows.map((r) => (
                  <PerCurrencyItem
                    key={r.currency}
                    tone={r.debt > 0 ? StudentColors.warn : undefined}
                  >
                    {formatMoneyCurrency(r.debt, r.currency)}
                  </PerCurrencyItem>
                ))}
              </PerCurrencyList>
            }
          />
          <KpiCard
            label={t('pages.studentDetail.kpi.paymentsCount')}
            value={paymentsCount}
          />
        </KpiGrid>
      </Wrap>
    );
  }

  return (
    <Wrap>
      <KpiGrid columns={4}>
        <KpiCard
          label={t('pages.studentDetail.kpi.budget')}
          value={formatMoneyCurrency(
            primary?.budget || 0,
            primary?.currency || '',
          )}
          subLabel={t('pages.studentDetail.kpi.agreementsActive', {
            count: agreementsActiveTotal,
          })}
        />
        <KpiCard
          label={t('pages.studentDetail.kpi.paid')}
          value={formatMoneyCurrency(
            primary?.paid || 0,
            primary?.currency || '',
          )}
          accent={StudentColors.success}
        />
        <KpiCard
          label={t('pages.studentDetail.kpi.debt')}
          value={formatMoneyCurrency(
            primary?.debt || 0,
            primary?.currency || '',
          )}
          accent={(primary?.debt || 0) > 0 ? StudentColors.warn : undefined}
        />
        <KpiCard
          label={t('pages.studentDetail.kpi.paymentsCount')}
          value={paymentsCount}
        />
      </KpiGrid>
    </Wrap>
  );
});

const Wrap = styled.div`
  margin-top: 16px;
`;

const PerCurrencyList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const PerCurrencyItem = styled.div<{ tone?: string }>`
  font-size: 16px;
  font-weight: 700;
  color: ${({ tone }) => tone || StudentColors.textPrimary};
`;
